// Admin panel — most-clicked sub-services across the Landing page.
// Reveals what visitors actually want before they ever submit a booking,
// helping the business prioritise capacity & marketing per sub-service.
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { MousePointerClick, TrendingUp, Loader2 } from "lucide-react";

const RANGES = [7, 30, 90];

export default function AdminSubClicksStats() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [methodMix, setMethodMix] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get(`/admin/sub-clicks/stats?days=${days}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Fehler"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    api.get(`/admin/bookings/method-mix?days=${days}`)
      .then((r) => { if (!cancelled) setMethodMix(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [days]);

  return (
    <div data-testid="admin-subclicks-stats">
      <div className="flex items-center gap-2 mb-6">
        <span className="text-xs font-bold tracking-widest uppercase text-neutral-500">Zeitraum:</span>
        {RANGES.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={`px-3 py-1.5 text-xs font-bold tracking-wide border ${
              days === d
                ? "bg-[#FF5A00] text-white border-[#FF5A00]"
                : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500"
            }`}
            data-testid={`subclicks-range-${d}`}
          >
            {d} Tage
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-neutral-500 py-8" data-testid="subclicks-loading">Laden…</div>}
      {error && <div className="text-sm text-red-600 bg-red-50 px-4 py-3 mb-6">{error}</div>}

      {data && !loading && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            <SummaryCard icon={MousePointerClick} label="Gesamt-Klicks" value={data.total_clicks} />
            <SummaryCard icon={TrendingUp} label="Buchungen daraus" value={data.total_bookings ?? 0} accent />
            <SummaryCard icon={TrendingUp} label="Conversion" value={`${data.overall_conversion_rate_pct ?? 0}%`} />
            <SummaryCard icon={TrendingUp} label="Sub-Services" value={data.by_sub.length} />
          </div>

          {/* Per-parent breakdown */}
          {data.by_service.length > 0 && (
            <>
              <h3 className="text-sm font-bold tracking-widest uppercase text-neutral-500 mb-3">Pro Hauptkategorie</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mb-8">
                {data.by_service.map((s) => (
                  <div key={s.service} className="border border-neutral-200 p-3" data-testid={`subclicks-cat-${s.service}`}>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">{s.service}</div>
                    <div className="text-2xl font-black tabular-nums">{s.clicks ?? s.count ?? 0}</div>
                    <div className="text-[10px] text-emerald-700 font-bold mt-0.5">
                      {(s.bookings ?? 0)} Buchungen · {(s.conversion_rate_pct ?? 0)}%
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Full ranking */}
          <h3 className="text-sm font-bold tracking-widest uppercase text-neutral-500 mb-3">Top Sub-Services</h3>
          <div className="border border-neutral-200 overflow-x-auto" data-testid="subclicks-table">
            {data.by_sub.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-neutral-500" data-testid="subclicks-empty">
                Noch keine Klicks im Zeitraum.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <Th>Hauptkategorie</Th>
                    <Th>Sub-Service</Th>
                    <Th className="text-end">Klicks</Th>
                    <Th className="text-end">Buchungen</Th>
                    <Th className="text-end">Conversion</Th>
                    <Th>Letzter Klick</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_sub.map((row, i) => {
                    const rate = row.conversion_rate_pct ?? 0;
                    const rateClass = rate >= 20
                      ? "bg-emerald-100 text-emerald-800"
                      : rate >= 5
                      ? "bg-amber-100 text-amber-800"
                      : "bg-neutral-100 text-neutral-600";
                    return (
                      <tr key={`${row.service}-${i}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                        <td className="px-4 py-3 font-mono text-[11px] font-bold text-neutral-900">{row.service}</td>
                        <td className="px-4 py-3 text-neutral-700">{row.sub_label}</td>
                        <td className="px-4 py-3 text-end tabular-nums font-bold text-[#FF5A00]">{row.clicks ?? row.count ?? 0}</td>
                        <td className="px-4 py-3 text-end tabular-nums font-bold text-emerald-700">{row.bookings ?? 0}</td>
                        <td className="px-4 py-3 text-end">
                          <span className={`inline-block px-2 py-0.5 text-[11px] font-bold ${rateClass}`}>
                            {rate}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-neutral-500 tabular-nums">{(row.last_at || "—").slice(0, 16).replace("T", " ")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <p className="text-[11px] text-neutral-400 mt-3 leading-relaxed">
            Klicks = Besucher klickten auf einen Unterpunkt einer Servicekarte. Buchungen = davon haben tatsächlich gebucht.
            Conversion = Buchungen ÷ Klicks (höher = bessere Page-Sub-Service-Passung).
          </p>
        </>
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-2 text-[11px] font-bold tracking-widest uppercase text-neutral-500 text-start ${className}`}>{children}</th>
);

const SummaryCard = ({ icon: Icon, label, value, accent = false }) => (
  <div className={`p-4 border ${accent ? "border-[#FF5A00] bg-orange-50" : "border-neutral-200 bg-white"}`}>
    <div className="flex items-center gap-2 mb-1.5">
      <Icon size={14} className={accent ? "text-[#FF5A00]" : "text-neutral-500"} />
      <span className="text-[10px] font-bold tracking-widest uppercase text-neutral-500">{label}</span>
    </div>
    <div className={`text-3xl font-black tabular-nums ${accent ? "text-[#FF5A00]" : "text-neutral-900"}`}>{value}</div>
  </div>
);
